import { CjTool } from '../../../utilities/cj-tool';
import { CjContainer, CjShape } from '../../model/cj-factory';
import { CJ_CONTAINER_NAME } from '../../model/cj-factory/cj-obj-name.collection';
import { LayoutDataManager } from '../data/layout-data.manager';
import { ILayoutEditorManagerBase } from '../layout-editor.manager';
import { ImageEditManager } from '../image-edit/image-edit.manager';

export type Point = {
  x: number;
  y: number;
};

type FocusRect = {
  topLeft: Point;
  topRight: Point;
  bottomLeft: Point;
  bottomRight: Point;
  center: Point;
};

type ContainerType = 'text' | 'png' | 'photoFrame' | 'photo';

type TypedContainer = {
  container: CjContainer;
  type: ContainerType;
};

type SnapOffset = {
  offsetX?: number;
  offsetY?: number;
};

type GuideSource = 'page' | 'object';

type GuidePos = {
  x?: {
    pos: number;
    source: GuideSource;
  };
  y?: {
    pos: number;
    source: GuideSource;
  };
};

type SnapResult = {
  offset: SnapOffset;
  guidePos: GuidePos;
};

type VisibleTarget = CjContainer | createjs.DisplayObject | undefined;

const NORMAL_LINE_COLOR = '#57d4f8';
const PAGE_GUIDE_LINE_COLOR = '#f84ef3';

export class SmartGuideManager implements ILayoutEditorManagerBase {
  private stage: CjContainer;
  private pageId: string;
  private layoutData!: LayoutDataManager;
  private imageEdit!: ImageEditManager;
  private pageWidth = 0;
  private pageHeight = 0;
  private guideCache: FocusRect[] | null = null;
  private lineX = new CjShape();
  private lineY = new CjShape();
  private PAGE_GUIDE_POS_X: { pos: number; source: 'page' }[] = [];
  private PAGE_GUIDE_POS_Y: { pos: number; source: 'page' }[] = [];
  private prevGuidePos: GuidePos = { x: undefined, y: undefined };

  constructor(stage: CjContainer, pageId: string) {
    this.stage = stage;
    this.pageId = pageId;
  }

  di(layoutData: LayoutDataManager, imageEdit: ImageEditManager) {
    this.layoutData = layoutData;
    this.imageEdit = imageEdit;
  }

  initialize(): void {
    this.pageWidth = this.imageEdit.templateVirtual.w;
    this.pageHeight = this.imageEdit.templateVirtual.h;
    // this.pageWidth = album.virtual.width;
    // this.pageHeight = album.virtual.height;
    this.PAGE_GUIDE_POS_X = [
      { pos: 0, source: 'page' },
      { pos: this.pageWidth / 2, source: 'page' },
      { pos: this.pageWidth, source: 'page' },
    ];
    this.PAGE_GUIDE_POS_Y = [
      { pos: 0, source: 'page' },
      { pos: this.pageHeight / 2, source: 'page' },
      { pos: this.pageHeight, source: 'page' },
    ];
  }

  destroy(): void {
    this.clearGuideCache();
    this.pageWidth = 0;
    this.pageHeight = 0;
    this.PAGE_GUIDE_POS_X = [];
    this.PAGE_GUIDE_POS_Y = [];
    this.prevGuidePos = { x: undefined, y: undefined };
  }

  update(stage: CjContainer): void {
    this.stage = stage;
  }

  clearGuideCache() {
    this.guideCache = null;
    this.stage.removeChild(this.lineX, this.lineY);
  }

  applySnap(
    current: number,
    base: number,
    offset: number | undefined,
    releaseThreshold: number = 20
  ): number {
    if (offset !== undefined) {
      if (Math.abs(current - base) >= releaseThreshold) {
        return base;
      }
      return current + offset;
    } else {
      return base;
    }
  }

  calculateGuideSnap(moving: TypedContainer): SnapOffset {
    const cache = this.getGuideCache(moving);
    const movingRect = this.getRect(moving);
    const {
      offset,
      guidePos,
    } = this.compareRects(movingRect, cache);
    this.drawGuideLines(guidePos);
    return offset;
  }

  private drawGuideLines(pos: GuidePos) {
    const prev = this.prevGuidePos;
    // Xガイドラインの更新が必要か判定
    const shouldUpdateX = pos.x?.pos !== prev.x?.pos || pos.x?.source !== prev.x?.source;
    // Yガイドラインの更新が必要か判定
    const shouldUpdateY = pos.y?.pos !== prev.y?.pos || pos.y?.source !== prev.y?.source;
    if (shouldUpdateX) {
      this.updateLine(this.lineX, 'x', pos.x);
    }
    if (shouldUpdateY) {
      this.updateLine(this.lineY, 'y', pos.y);
    }
    this.prevGuidePos = {
      x: pos.x ? { ...pos.x } : undefined,
      y: pos.y ? { ...pos.y } : undefined,
    };
  }

  private updateLine(
    line: createjs.Shape,
    axis: 'x' | 'y',
    guide?: { pos: number; source: GuideSource }
  ) {
    if (guide) {
      line.visible = true;
      const color = guide.source === 'page' ? PAGE_GUIDE_LINE_COLOR : NORMAL_LINE_COLOR;
      line.graphics.clear().s(color).ss(2);
      if (axis === 'x') {
        line.graphics.mt(guide.pos, 0).lt(guide.pos, this.pageHeight);
      } else {
        line.graphics.mt(0, guide.pos).lt(this.pageWidth, guide.pos);
      }
    } else {
      line.visible = false;
    }
  }

  private compareRects(moving: FocusRect, guides: FocusRect[]): SnapResult {
    const SNAP_THRESHOLD = 5;
    const movingPoints = [
      moving.topLeft,
      moving.topRight,
      moving.center,
      moving.bottomLeft,
      moving.bottomRight,
    ];
    // スナップ候補を事前に構築
    const snapCandidatesX: { pos: number; source: GuideSource }[] = [...this.PAGE_GUIDE_POS_X];
    const snapCandidatesY: { pos: number; source: GuideSource }[] = [...this.PAGE_GUIDE_POS_Y];
    for (const g of guides) {
      const points = [g.topLeft, g.topRight, g.center, g.bottomLeft, g.bottomRight];
      for (const pt of points) {
        snapCandidatesX.push({ pos: pt.x, source: 'object' });
        snapCandidatesY.push({ pos: pt.y, source: 'object' });
      }
    }
    // 最近接スナップを探索
    let guidePos: GuidePos = {};
    let offsetX: number | undefined;
    let offsetY: number | undefined;
    for (const mp of movingPoints) {
      if (offsetX === undefined) {
        for (const c of snapCandidatesX) {
          const diff = c.pos - mp.x;
          if (Math.abs(diff) <= SNAP_THRESHOLD) {
            offsetX = diff;
            guidePos.x = c;
            break;
          }
        }
      }
      if (offsetY === undefined) {
        for (const c of snapCandidatesY) {
          const diff = c.pos - mp.y;
          if (Math.abs(diff) <= SNAP_THRESHOLD) {
            offsetY = diff;
            guidePos.y = c;
            break;
          }
        }
      }
      if (offsetX !== undefined && offsetY !== undefined) break;
    }
    return {
      offset: { offsetX, offsetY },
      guidePos,
    };
  }

  private getGuideCache(
    moving: TypedContainer,
    limit: number = 10,
  ): FocusRect[] {
    if (!this.guideCache) {
      const graphics: CjContainer[] = this.layoutData.selector('getFreeGraphics', this.pageId);
      const texts: CjContainer[] = this.layoutData.selector('getTexts', this.pageId);
      const editors: CjContainer[] = this.layoutData.selector('getEditors', this.pageId);
      const containers: TypedContainer[] = [
        ...graphics.map(c => ({ container: c, type: 'png' as const })),
        ...texts.map(c => ({ container: c, type: 'text' as const })),
        ...editors.filter(c => c.visible).map(c => ({ container: c, type: 'photoFrame' as const })),
      ];
      this.guideCache = this.buildGuideCache(containers, moving, limit);
      this.stage.addChild(this.lineX, this.lineY);
    }
    return this.guideCache;
  }

  private buildGuideCache(
    containers: TypedContainer[],
    moving: TypedContainer,
    limit: number = 10,
  ): FocusRect[] {
    const movingRect = this.getRect(moving);
    const result: { container: CjContainer; type: ContainerType; dist: number }[] = [];
    for (const c of containers) {
      if (c.container === moving.container) continue;
      const dx = c.container.x - movingRect.center.x;
      const dy = c.container.y - movingRect.center.y;
      const dist = dx * dx + dy * dy;
      let inserted = false;
      for (let i = 0; i < result.length; i++) {
        if (dist < result[i].dist) {
          result.splice(i, 0, { container: c.container, type: c.type, dist });
          inserted = true;
          break;
        }
      }
      if (!inserted) {
        result.push({ container: c.container, type: c.type, dist });
      }
      if (result.length > limit) {
        result.pop();
      }
    }
    return result.map(c => this.getRect({ container: c.container, type: c.type }));
  }

  private findChildrenByName(
    container: createjs.Container | undefined,
    name: string
  ): createjs.DisplayObject[] {
    if (!container) return [];
    return container.children.filter(c => c.name === name);
  }

  private getRect(
    target: TypedContainer,
  ): FocusRect {
    const {
      container,
      type,
    } = target;
    if (type === 'text') {
      const data = container.getTransformedBounds();
      const rectX = data.x - 10;
      const rectY = data.y - 10;
      const rectW = data.width + 20;
      const rectH = data.height + 20;
      return {
        topLeft: { x: rectX, y: rectY },
        topRight: { x: rectX + rectW, y: rectY },
        bottomLeft: { x: rectX, y: rectY + rectH },
        bottomRight: { x: rectX + rectW, y: rectY + rectH },
        center: { x: rectX + rectW / 2, y: rectY + rectH / 2 },
      };
    } else if (type === 'photoFrame') {
      // 一時的に中の写真を非表示にして本体のboundsを取得
      const photo = CjTool.getPhotoContainer(container);
      const data = this.withHidden(photo, () => container.getTransformedBounds());
      return {
        topLeft: { x: data.x, y: data.y },
        topRight: { x: data.x + data.width, y: data.y },
        bottomLeft: { x: data.x, y: data.y + data.height },
        bottomRight: { x: data.x + data.width, y: data.y + data.height },
        center: { x: data.x + data.width / 2, y: data.y + data.height / 2 },
      };
    } else if (type === 'photo') {
      // 座標が枠内での座標だったので、写真枠を取得する
      const photoFrames: CjContainer[] = this.layoutData.selector('getEditors', this.pageId);
      const matchedFrame = photoFrames.find(frame => {
        if (!frame.visible) return false;
        const photo = CjTool.getPhotoContainer(frame);
        return photo === container;
      });
      if (!matchedFrame) {
        throw new Error('写真枠の取得に失敗しました!!!');
      }
      const imageArea = CjTool.getImageAreaContainer(matchedFrame);
      const rotate = CjTool.getRotateContainer(matchedFrame);
      // 一時的に 写真 ・ 装飾 ・ 背景 を非表示にして本体のboundsを取得
      const photo = CjTool.getPhotoContainer(matchedFrame);
      const frames = this.findChildrenByName(imageArea, CJ_CONTAINER_NAME.frame);
      const backs = this.findChildrenByName(rotate, CJ_CONTAINER_NAME.back);
      const frameData = this.withHidden([photo, ...frames, ...backs], () => matchedFrame.getTransformedBounds());
      // 一時的にフォーカスを非表示にして本体のboundsを取得
      const focus = container.getChildByName(CJ_CONTAINER_NAME.focus.rotate);
      const data = this.withHidden(focus, () => container.getBounds());
      const frameScale = CjTool.getScaleContainer(matchedFrame);
      // 中心点の絶対座標
      const frameExtraW = frameScale.originalBounds.width * (imageArea.scaleX - 1);
      const frameExtraH = frameScale.originalBounds.height * (imageArea.scaleY - 1);
      const frameOffsetX = (frameExtraW / 2) * frameScale.scaleX;
      const frameOffsetY = (frameExtraH / 2) * frameScale.scaleY;
      const abcCx = frameData.x + frameOffsetX + (container.x * frameScale.scaleX);
      const abcCy = frameData.y + frameOffsetY + (container.y * frameScale.scaleY);
      // 枠と写真の scale を考慮
      const photoW = (data.width * container.scaleX) * frameScale.scaleX;
      const photoH = (data.height * container.scaleY) * frameScale.scaleY;
      const offsetX = photoW / 2;
      const offsetY = photoH / 2;
      let corners = [
        { x: abcCx - offsetX, y: abcCy - offsetY },
        { x: abcCx + offsetX, y: abcCy - offsetY },
        { x: abcCx - offsetX, y: abcCy + offsetY },
        { x: abcCx + offsetX, y: abcCy + offsetY },
      ];
      // 枠の回転を考慮
      if (rotate.isRotate) {
        // 時計回り90°
        const rotatedX = (frameData.x + frameData.width) + (-container.y * frameScale.scaleX) - frameOffsetY;
        const rotatedY = frameData.y + (container.x * frameScale.scaleY) + frameOffsetX;
        corners = [
          { x: rotatedX - offsetX, y: rotatedY - offsetY },
          { x: rotatedX + offsetX, y: rotatedY - offsetY },
          { x: rotatedX - offsetX, y: rotatedY + offsetY },
          { x: rotatedX + offsetX, y: rotatedY + offsetY },
        ];
        // 写真の回転を考慮 (枠の回転を引く)
        corners = this.rotateCorners(corners, rotatedX, rotatedY, container.rotation - rotate.rotation);
        return {
          topLeft: corners[0],
          topRight: corners[1],
          bottomLeft: corners[2],
          bottomRight: corners[3],
          center: { x: rotatedX, y: rotatedY },
        };
      }
      // 写真の回転を考慮
      corners = this.rotateCorners(corners, abcCx, abcCy, container.rotation);
      return {
        topLeft: corners[0],
        topRight: corners[1],
        bottomLeft: corners[2],
        bottomRight: corners[3],
        center: { x: abcCx, y: abcCy },
      };
    }
    // 一時的にフォーカスを非表示にして本体のboundsを取得
    const focus = container.getChildByName(CJ_CONTAINER_NAME.focus.rotate);
    const data = this.withHidden(focus, () => container.getBounds());
    const offsetX = container.regX;
    const offsetY = container.regY;
    let corners = [
      { x: data.x - offsetX, y: data.y - offsetY },
      { x: data.x + data.width - offsetX, y: data.y - offsetY },
      { x: data.x - offsetX, y: data.y + data.height - offsetY },
      { x: data.x + data.width - offsetX, y: data.y + data.height - offsetY },
    ];
    // scale を考慮
    const scaleX = container.scaleX;
    const scaleY = container.scaleY;
    corners = corners.map(({ x, y }) => ({
      x: x * scaleX,
      y: y * scaleY,
    }));
    // 回転を考慮
    const cx = container.x;
    const cy = container.y;
    const theta = (container.rotation) * (Math.PI / 180);
    const cos = Math.cos(theta);
    const sin = Math.sin(theta);
    corners = corners.map(({ x, y }) => ({
      x: cx + (x * cos - y * sin),
      y: cy + (x * sin + y * cos),
    }));
    return {
      topLeft: corners[0],
      topRight: corners[1],
      bottomLeft: corners[2],
      bottomRight: corners[3],
      center: { x: cx, y: cy },
    };
  }

  private withHidden<T>(containers: VisibleTarget | VisibleTarget[], fn: () => T): T {
    const targets = Array.isArray(containers) ? containers : [containers];
    // undefined を除外
    const validTargets = targets.filter((c): c is CjContainer | createjs.DisplayObject => !!c);
    const prevStates = validTargets.map(c => c.visible);
    try {
      validTargets.forEach(c => c.visible = false);
      return fn();
    } finally {
      validTargets.forEach((c, i) => c.visible = prevStates[i]);
    }
  }

  private rotateCorners(
    corners: { x: number; y: number }[],
    cx: number,
    cy: number,
    rotationDeg: number
  ): { x: number; y: number }[] {
    if (rotationDeg === 0) return corners;
    const theta = rotationDeg * (Math.PI / 180);
    const cos = Math.cos(theta);
    const sin = Math.sin(theta);
    return corners.map(({ x, y }) => {
      const dx = x - cx;
      const dy = y - cy;
      return {
        x: cx + dx * cos - dy * sin,
        y: cy + dx * sin + dy * cos,
      };
    });
  }
}
